import React, { Fragment, useEffect, useState } from 'react'

import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import Confirmation from '@components/UI/Confirmation'
import { FooterButtonWrapper } from '@components/UI/Confirmation/Confirmation.styled'
import TrackedRoute from '@components/UI/TrackedRoute/TrackedRoute'
import { IAnalyticsTrackingContextProps } from '@context/AnalyticsTracking'
import { ICampaignContextProps } from '@context/Campaign'
import { IModalContextProps } from '@context/Modal'
import { EventTrackingNames } from '@enums'
import { withAnalyticsTracking, withCampaign, withModal } from '@hocs'
import { usePrevious } from '@hooks/usePrevious'
import { IResponseError } from '@lib/error'
import { IApplicationState } from '@store'
import { clearErrors, selectisAdding, selectMemberErrors } from '@store/members'
import Theme from '@theme'
import { Button, Icon } from '@tribegroup'
import { InboxMemberAddForm } from '../Form/Form'

interface IProps
  extends IAnalyticsTrackingContextProps,
    ICampaignContextProps,
    IModalContextProps {}

export const InboxMemberInvite: React.FC<IProps> = ({
  showModal,
  hideModal,
  getCampaignTrackingProps,
}) => {
  const dispatch = useDispatch()
  const isAdding = useSelector((state: IApplicationState) => selectisAdding(state))
  const errors = useSelector((state: IApplicationState) => selectMemberErrors(state)) as
    | IResponseError
    | undefined

  const [showErrorMessage, setShowErrorMessage] = useState(false)

  const prevIsAdding = usePrevious(isAdding)
  const prevErrors = usePrevious(errors)

  useEffect(() => {
    if (prevIsAdding && !isAdding) {
      hideModal()
    }
  }, [isAdding])

  useEffect(() => {
    if (!prevErrors && errors) {
      setShowErrorMessage(true)
    }
  }, [errors])

  const openModal = () => {
    showModal(InboxMemberAddForm)
  }

  const hideErrorMessage = () => {
    setShowErrorMessage(false)
    setTimeout(() => dispatch(clearErrors()), 500)
  }

  const errorMessage = errors && errors.messages && errors.messages[0]
  const hasErrorCodeOrMessage =
    errorMessage && ((errorMessage as any).message || (errorMessage as any).error_code)

  return (
    <Fragment>
      <TrackedRoute
        eventName={EventTrackingNames.DisplayMemberInviteModal}
        properties={getCampaignTrackingProps()}
      >
        <Icon
          glyph="circle-plus-heavy"
          color={Theme.grey900Color}
          data-cy-element="invite-member"
          data-testid="invite-member"
          onClick={openModal}
        />
      </TrackedRoute>
      <Confirmation
        title={
          hasErrorCodeOrMessage ? (
            <FormattedMessage
              id={`inbox.sidebar.members.form.error.${
                errorMessage && (errorMessage as any).error_code
              }`}
              defaultMessage={errorMessage && (errorMessage as any).message}
            />
          ) : (
            <FormattedMessage id="inbox.sidebar.members.form.error.generic" />
          )
        }
        content={<FormattedMessage id="inbox.sidebar.members.form.error.body" />}
        footerButtons={
          <Fragment>
            <FooterButtonWrapper>
              <Button
                scheme="primary"
                onClick={hideErrorMessage}
                data-cy-element="error-modal-okay-button"
              >
                <FormattedMessage id="core.text.okay" />
              </Button>
            </FooterButtonWrapper>
          </Fragment>
        }
        isOpen={showErrorMessage}
        onCloseIconClick={hideErrorMessage}
      />
    </Fragment>
  )
}

export default withModal(withCampaign(withAnalyticsTracking(InboxMemberInvite)))
